import { useMemo } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'

import { Flex, Text } from '@buffer-mono/popcorn'

import type { TakeawayCtaTrackingContext } from '../../hooks/useTrackTakeawayCtaClicked'
import { CategoryBadge, ChannelBadge } from './TakeawayBadge'
import { TakeawayCtas } from './TakeawayCtas'
import { TakeawayFeedbackControls } from './TakeawayFeedbackControls'
import { TakeawayInsightsLink } from './TakeawayInsightsLink'
import {
  useTakeawayChannelService,
  useTakeawayChannels,
  useTakeawayVariant,
} from './TakeawaysContext'
import type { TakeawayItem } from './types'

import styles from './Takeaways.module.css'

export function TakeawayCard({
  takeaway,
  colorIndex,
  cardPosition,
  collapsed = false,
}: {
  takeaway: TakeawayItem
  colorIndex: number
  cardPosition: number
  collapsed?: boolean
}): JSX.Element | null {
  const variant = useTakeawayVariant()
  const channels = useTakeawayChannels(takeaway.channelIds)
  const sectionService = useTakeawayChannelService()

  const ctaChannelId = takeaway.channelIds?.[0]
  const effectiveService = channels[0]?.service ?? sectionService

  const tracking = useMemo<TakeawayCtaTrackingContext>(
    () => ({
      takeawayId: takeaway.id ?? null,
      channelId: ctaChannelId ?? null,
      serviceType: effectiveService ?? null,
      category: takeaway.category,
      cardPosition,
      ...(takeaway.metadata?.createPostType && {
        subCategory: takeaway.metadata.createPostType,
      }),
    }),
    [
      takeaway.id,
      ctaChannelId,
      effectiveService,
      takeaway.category,
      cardPosition,
      takeaway.metadata?.createPostType,
    ],
  )

  // Bail out rather than fall back to an empty id that would build invalid routes.
  if (!ctaChannelId) {
    return null
  }

  // Every takeaway names a channel, so only the page decides which badge fits:
  // naming the channel is noise where the whole page already is that channel.
  const showChannels = variant === 'cross-channel' && channels.length > 0

  // Only cross-channel Insights renders links to single-channel pages.
  const shouldShowLinkToChannelInsights =
    variant === 'cross-channel' &&
    takeaway.channelIds?.length === 1 &&
    channels.length === 1

  return (
    <Flex direction="column" className={styles.takeawayCard}>
      <div
        className={clsx(
          styles.cardHeader,
          collapsed && styles.cardHeaderCollapsed,
        )}
      >
        {showChannels ? (
          <ChannelBadge channelIds={takeaway.channelIds} />
        ) : (
          <CategoryBadge
            category={takeaway.category}
            createPostType={takeaway.metadata?.createPostType}
            colorIndex={colorIndex}
          />
        )}
        {!collapsed && (
          <div className={styles.feedbackControls}>
            {takeaway.id && (
              <TakeawayFeedbackControls
                takeawayId={takeaway.id}
                channelId={ctaChannelId}
                feedback={takeaway.feedback}
                trackingContext={tracking}
              />
            )}
          </div>
        )}
        {/* AI-generated insight text — treated as content, not translated. */}
        <Text
          size="md"
          weight="semibold"
          className={styles.title}
          translate="no"
        >
          {takeaway.title}
        </Text>
      </div>
      <div
        className={clsx(
          styles.cardBodyCollapse,
          collapsed && styles.cardBodyCollapsed,
        )}
        aria-hidden={collapsed || undefined}
      >
        <div className={styles.cardBodyClip}>
          <Flex direction="column" gap="sm" className={styles.cardBody}>
            <Text size="md" color="subtle" translate="no">
              {takeaway.description}
            </Text>
            <div className={styles.cardFooter}>
              <TakeawayCtas
                takeaway={takeaway}
                ctaChannelId={ctaChannelId}
                channelService={effectiveService}
                tracking={tracking}
              />
              {shouldShowLinkToChannelInsights && (
                <TakeawayInsightsLink
                  channelId={channels[0].id}
                  channelName={channels[0].name}
                  trackingContext={tracking}
                />
              )}
            </div>
          </Flex>
        </div>
      </div>
    </Flex>
  )
}
